import { Component, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { postClientError } from '@vinyl/shared';
import { api } from '../lib/api.ts';
import { webBuild } from '../lib/build.ts';
import { Button } from '../components/ui/button.tsx';

/** Sends an unexpected UI error to the server log. Never throws. */
export function reportClientError(error: unknown): void {
  const err = error instanceof Error ? error : new Error(String(error));
  void api(postClientError, {
    body: {
      message: err.message.slice(0, 10_000),
      ...(err.stack ? { stack: err.stack.slice(0, 20_000) } : {}),
      path: window.location.pathname.slice(0, 500),
      version: webBuild.version.slice(0, 50),
    },
  }).catch(() => {});
}

let installed = false;
/** Reports errors that escape React too (window.onerror and unhandled rejections). */
export function installGlobalErrorReporting(): void {
  if (installed) return;
  installed = true;
  window.addEventListener('error', (event) => reportClientError(event.error ?? event.message));
  window.addEventListener('unhandledrejection', (event) => reportClientError(event.reason));
}

function ErrorScreen() {
  const { t } = useTranslation();
  return (
    <div role="alert" className="flex min-h-dvh flex-col items-center justify-center gap-4 px-4 text-center">
      <h1 className="text-2xl">{t('errorScreen.title')}</h1>
      <p className="max-w-sm text-muted-foreground">{t('errorScreen.text')}</p>
      <Button onClick={() => window.location.reload()}>{t('common.reload')}</Button>
    </div>
  );
}

/** Shows a reload screen and reports the error when a component throws. */
export class ErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override componentDidCatch(error: unknown) {
    reportClientError(error);
  }

  override render() {
    return this.state.failed ? <ErrorScreen /> : this.props.children;
  }
}
